import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHospitalPartners, QUERY_KEYS } from "@/hooks/use-queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import type { HealthcareProvider, HospitalPartnerStats } from "@shared/schema";
import { 
  Building2, 
  TrendingUp, 
  Package, 
  Truck, 
  CreditCard, 
  DollarSign, 
  Phone,
  Mail,
  User,
  BarChart3,
  ChevronRight,
  Edit,
  Trash2,
  Clock
} from "lucide-react";
import { StatusBadge, type DashboardData } from "./shared";
import { PartnerFormDialog } from "./partner-form";
import { StatusChangeDialog } from "./status-change";

function RevenueChart({ stats }: { stats: HospitalPartnerStats[] }) {
  const { t } = useTranslation();
  
  const chartData = stats
    .slice()
    .sort((a, b) => (a.year * 12 + a.month) - (b.year * 12 + b.month))
    .map(stat => ({
      name: format(new Date(stat.year, stat.month - 1), 'MMM yyyy'),
      orders: stat.ordersCreated,
      delivered: stat.ordersDelivered,
      revenue: (stat.paymentCollected || 0) / 100,
    }));
  
  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        {t('hospitalPartners.noChartData', 'No data available for chart')}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium mb-4">{t('hospitalPartners.ordersOverTime', 'Orders Over Time')}</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="name" className="text-xs" />
            <YAxis className="text-xs" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Bar dataKey="orders" name={t('hospitalPartners.ordersCreated', 'Orders Created')} fill="hsl(var(--primary))" />
            <Bar dataKey="delivered" name={t('hospitalPartners.delivered', 'Delivered')} fill="hsl(142 76% 36%)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div>
        <h4 className="text-sm font-medium mb-4">{t('hospitalPartners.revenueOverTime', 'Revenue Over Time')}</h4>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="name" className="text-xs" />
            <YAxis className="text-xs" tickFormatter={(value) => `$${value}`} />
            <Tooltip 
              formatter={(value: number) => [`$${value.toLocaleString()}`, t('hospitalPartners.revenue', 'Revenue')]}
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              name={t('hospitalPartners.revenue', 'Revenue')} 
              stroke="hsl(45 93% 47%)" 
              strokeWidth={2}
              dot={{ fill: 'hsl(45 93% 47%)' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PartnerDashboard({ partnerId, onBack, providers }: { partnerId: string; onBack: () => void; providers: HealthcareProvider[] }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  
  const { data: dashboardData, isLoading, error } = useQuery<DashboardData>({
    queryKey: ["/api/hospital-partners", partnerId, "dashboard"],
    queryFn: async () => {
      const res = await fetch(`/api/hospital-partners/${partnerId}/dashboard`, { credentials: 'include' });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const { data: allPartners } = useHospitalPartners();
  
  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/hospital-partners/${partnerId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.hospitalPartners });
      toast({ title: t('hospitalPartners.deleted', 'Partner Deleted') });
      onBack();
    },
    onError: (error: any) => {
      toast({ title: t('common.error', 'Error'), description: error.message, variant: "destructive" });
    },
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading', 'Loading...')}</div>
      </div>
    );
  }
  
  if (error || !dashboardData) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-destructive">{t('common.error', 'Error loading data')}</div>
        <Button variant="outline" onClick={onBack}>{t('common.back', 'Back')}</Button>
      </div>
    );
  }
  
  const { partner, provider, stats, paymentPlans } = dashboardData;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="button-back">
            <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
            {t('common.back', 'Back')}
          </Button>
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              {partner.partnerName}
            </h2>
            <p className="text-muted-foreground">{partner.partnerCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={partner.status} />
          {user?.role === "admin" && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setStatusDialogOpen(true)}
                data-testid="button-change-status"
              >
                <Clock className="h-4 w-4 mr-1" />
                {t('hospitalPartners.changeStatus', 'Change Status')}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setEditDialogOpen(true)}
                data-testid="button-edit-partner"
              >
                <Edit className="h-4 w-4 mr-1" />
                {t('common.edit', 'Edit')}
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                data-testid="button-delete-partner"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {t('common.delete', 'Delete')}
              </Button>
            </>
          )}
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.totalOrders', 'Total Orders')}</p>
                <p className="text-2xl font-bold">{partner.totalOrders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.deliveries', 'Deliveries')}</p>
                <p className="text-2xl font-bold">{partner.totalDeliveries || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.paymentPlans', 'Payment Schedules')}</p>
                <p className="text-2xl font-bold">{partner.totalPaymentPlans || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">{t('hospitalPartners.revenue', 'Revenue')}</p>
                <p className="text-2xl font-bold">${((partner.totalRevenue || 0) / 100).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('hospitalPartners.partnerDetails', 'Partner Details')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {provider && (
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{provider.name}</p>
                  <p className="text-muted-foreground">{provider.address}, {provider.city}, {provider.state}</p>
                </div>
              </div>
            )}
            {partner.contactName && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{partner.contactName}</span>
              </div>
            )}
            {partner.contactEmail && (
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{partner.contactEmail}</span>
              </div>
            )}
            {partner.contactPhone && (
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{partner.contactPhone}</span>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('hospitalPartners.contractTerms', 'Contract Terms')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('hospitalPartners.paymentTerms', 'Payment Terms')}</span>
              <span className="font-medium">NET {partner.paymentTermsDays}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('hospitalPartners.revenueShare', 'Revenue Share')}</span>
              <span className="font-medium">{partner.revenueSharePercent}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('hospitalPartners.minOrders', 'Min Orders/Month')}</span>
              <span className="font-medium">{partner.monthlyMinimumOrders}</span>
            </div>
            {partner.contractStartDate && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('hospitalPartners.contractStart', 'Start Date')}</span>
                <span className="font-medium">{format(new Date(partner.contractStartDate), 'MMM d, yyyy')}</span>
              </div>
            )}
            {partner.contractEndDate && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('hospitalPartners.contractEnd', 'End Date')}</span>
                <span className="font-medium">{format(new Date(partner.contractEndDate), 'MMM d, yyyy')}</span>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('common.notes', 'Notes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {partner.notes || t('hospitalPartners.noNotes', 'No notes available')}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="charts">
        <TabsList>
          <TabsTrigger value="charts" data-testid="tab-charts">
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('hospitalPartners.analytics', 'Analytics')}
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">
            <CreditCard className="h-4 w-4 mr-2" />
            {t('hospitalPartners.paymentPlans', 'Payment Schedules')}
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('hospitalPartners.monthlyStats', 'Monthly Stats')}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="charts">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('hospitalPartners.performanceCharts', 'Performance Charts')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RevenueChart stats={stats} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="plans">
          <Card>
            <CardContent className="pt-6">
              {paymentPlans.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('hospitalPartners.noPlans', 'No payment schedules found')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('paymentPlans.patient', 'Patient')}</TableHead>
                      <TableHead>{t('paymentPlans.originalBalance', 'Original')}</TableHead>
                      <TableHead>{t('paymentPlans.remaining', 'Remaining')}</TableHead>
                      <TableHead>{t('paymentPlans.status', 'Status')}</TableHead>
                      <TableHead>{t('paymentPlans.created', 'Created')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentPlans.map(plan => (
                      <TableRow key={plan.id} data-testid={`row-plan-${plan.id}`}>
                        <TableCell className="font-medium">{plan.serviceDescription || '-'}</TableCell>
                        <TableCell>${(plan.originalBalance / 100).toLocaleString()}</TableCell>
                        <TableCell>${(plan.remainingBalance / 100).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={plan.status === 'completed' ? 'default' : 'secondary'}>
                            {plan.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {plan.createdAt ? format(new Date(plan.createdAt), 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="stats">
          <Card>
            <CardContent className="pt-6">
              {stats.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('hospitalPartners.noStats', 'No statistics available yet')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('hospitalPartners.period', 'Period')}</TableHead>
                      <TableHead>{t('hospitalPartners.ordersCreated', 'Orders')}</TableHead>
                      <TableHead>{t('hospitalPartners.delivered', 'Delivered')}</TableHead>
                      <TableHead>{t('hospitalPartners.onTime', 'On Time')}</TableHead>
                      <TableHead>{t('hospitalPartners.collected', 'Collected')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.map(stat => (
                      <TableRow key={stat.id} data-testid={`row-stat-${stat.id}`}>
                        <TableCell className="font-medium">
                          {format(new Date(stat.year, stat.month - 1), 'MMMM yyyy')}
                        </TableCell>
                        <TableCell>{stat.ordersCreated}</TableCell>
                        <TableCell>{stat.ordersDelivered}</TableCell>
                        <TableCell>{stat.ordersOnTime}</TableCell>
                        <TableCell>${((stat.paymentCollected || 0) / 100).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {editDialogOpen && (
        <PartnerFormDialog
          providers={providers}
          existingPartners={allPartners || []}
          partner={partner}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSuccess={() => {}}
        />
      )}
      
      {statusDialogOpen && (
        <StatusChangeDialog
          partner={partner}
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
